import { useEffect, useRef, useState } from "react"
import { WorkflowReportSettings} from "../../types/types"
import { useLocation, useNavigate } from "react-router-dom"
import { downloadReport } from "../../service/scanAnalysisService"
import { UseAnalysisProps } from "./AnalysisDashboard.types"

/**
 * UseAnalysis is responsible for managing all the state and logic for the Analysis page component.
 * 
 * @param {Function} updateToken callback function to clear token from session storage
 */
const useAnalysis = (props: UseAnalysisProps) => {

  const { thzMetadata } = props

  const navigate = useNavigate()

  const location = useLocation()

  /**
   * Width of the display containers.
   */
  const [displayWidth, setDisplayWidth] = useState<number>(1)

  const [selectedSection, setSelectedSection] = useState<number>(0)

  const [selectedScan, setSelectedScan] = useState<number>(-1)

  const [selectedControl, setSelectedControl] = useState<number>(0)

  const [isDownloadingReport, setIsDownloadingReport] = useState<boolean>(false)

  const [reportError, setReportError] = useState<Error | undefined>(undefined)

  /**
   * Reference for the graph.
   */
  const graphRef = useRef<HTMLDivElement>(null) 

  /**
   * Calculate width of screen
   */
  useEffect(() => {    
    if (graphRef === undefined || graphRef.current === undefined) { return }    
    const size = graphRef.current
  
    if (size === null || size.offsetWidth === null) { return }
    setDisplayWidth(size.offsetWidth)
  }, [graphRef])

  useEffect(() => {
    if (thzMetadata === undefined) { return }
    if (thzMetadata.length === 0) { return }
    if (selectedScan !== -1) { return }
    const firstScan = thzMetadata[0].findIndex( heatmap => heatmap.calibration_start_p1 !== 0)
    setSelectedScan(firstScan)
}, [thzMetadata, selectedScan])

  const onSelectSection = (section: number) => {
    if (thzMetadata === undefined) { return }
    if (section > thzMetadata.length) { return }
    setSelectedSection(section)

    const selectedScan = thzMetadata[section].findIndex( heatmap => heatmap.calibration_start_p1 !== 0)
    onSelectScan(selectedScan)
  }

  const onSelectScan = (scan: number) => {
    if (thzMetadata === undefined) { return }
    if (selectedSection > thzMetadata.length) { return }
    if (scan >= thzMetadata[selectedSection].length) { return }
    
    if (thzMetadata[selectedSection][scan].calibration_start_p1 === 0) { return }
    setSelectedScan(scan)
  }

  const onDownloadReport = async (reportSettings: WorkflowReportSettings) => {
    const workflow = location.state
    if (workflow === undefined) {
      setReportError(new Error("Failed to find workflow"))
      return
    }
    setIsDownloadingReport(true)

    const res = await downloadReport(workflow.id ?? -1, reportSettings)
    
    if (res instanceof Error) {
      setReportError(res)
      setIsDownloadingReport(false)
      return
    }

    const blob = await res.blob()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
        
    link.download = `${workflow.pipe_id ?? "WorkflowReport"}.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    // const pdfUrl = URL.createObjectURL(blob)
    
    // // Open in new tab
    // const newTab = window.open(pdfUrl, '_blank')
    
    // // Clean up the URL after a delay
    // setTimeout(() => {
    //   URL.revokeObjectURL(pdfUrl)
    // }, 10000); // Clean up after 10 seconds
    
    // // Check if popup was blocked
    // if (!newTab) {
    //   alert('Popup blocked. Please allow popups for this site.')
    // }

    setIsDownloadingReport(false)
  }

  return [{
    graphRef,
    displayWidth,
    selectedSection,
    selectedScan,
    selectedControl,
    isDownloadingReport,
    reportError,
    onHideReportError: () => setReportError(undefined),

    onSelectSection: onSelectSection,
    onSelectScan,
    onSelectControl: setSelectedControl,
    onDownloadReport,
    onBack: () => navigate("/")
  }]
}

export default useAnalysis
